import { Button, Container, Typography } from '@app/components';
import { EmailApi } from '@app/domain/email/api/EmailApi';
import { FormControlLabel, Switch } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { toast } from 'react-toastify';

// Fase 2 (2026-08-18): reads/writes email_preference via the authenticated
// /private/auth/me/email-preferences endpoints, not the raw accountNotification/qrNotification/
// marketingNotification booleans on User anymore - those stopped being read by anything that
// decides whether mail actually goes out (EmailSendGuard), so this toggle would otherwise save
// successfully while having no real effect at all.
export const AccountEmailPreferences: React.FC = () => {
  const { t } = useTranslation();

  const preferencesQuery = useQuery({
    queryKey: ['myEmailPreferences'],
    queryFn: () => EmailApi.getMyEmailPreferences(),
    refetchOnWindowFocus: false,
  });
  const saved = preferencesQuery.data;

  const [emailPreferences, setEmailPreferences] = useState({
    qrNotification: true,
    trialLifecycle: true,
    tips: true,
  });

  useEffect(() => {
    if (!saved) {
      return;
    }
    setEmailPreferences({
      qrNotification: saved.qrNotification,
      trialLifecycle: saved.trialLifecycle,
      tips: saved.tips,
    });
  }, [saved]);

  const handleEmailPreferences = (key: keyof typeof emailPreferences, value: boolean) => {
    setEmailPreferences((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const savePreferences = useMutation({
    mutationFn: () => EmailApi.saveMyEmailPreferences(emailPreferences),
    onSuccess: () => {
      toast.success(t('toaster.changesSaved'));
    },
  });

  const disableSubmitButton =
    !saved ||
    (emailPreferences.qrNotification === saved.qrNotification &&
      emailPreferences.trialLifecycle === saved.trialLifecycle &&
      emailPreferences.tips === saved.tips);

  return (
    <Container className="flex flex-col gap-6 p-6 lg:rounded-xl">
      <Typography variant="m" className="font-bold">
        {t('account.sectionEmailPreferences.title')}
      </Typography>
      <div className="flex flex-col gap-4 flex-wrap lg:flex-row lg:gap-x-12">
        <div>
          <FormControlLabel
            disabled
            checked
            control={<Switch />}
            label={t('account.sectionEmailPreferences.accountNotifications')}
          />
        </div>
        <div>
          <FormControlLabel
            onChange={() => {
              handleEmailPreferences('qrNotification', !emailPreferences.qrNotification);
            }}
            checked={emailPreferences.qrNotification}
            control={<Switch />}
            label={t('account.sectionEmailPreferences.qrNotifications')}
          />
        </div>
        <div>
          <FormControlLabel
            onChange={() => {
              handleEmailPreferences('trialLifecycle', !emailPreferences.trialLifecycle);
            }}
            checked={emailPreferences.trialLifecycle}
            control={<Switch />}
            label={t('account.sectionEmailPreferences.trialLifecycle')}
          />
        </div>
        <div>
          <FormControlLabel
            onChange={() => {
              handleEmailPreferences('tips', !emailPreferences.tips);
            }}
            checked={emailPreferences.tips}
            control={<Switch />}
            label={t('account.sectionEmailPreferences.tips')}
          />
        </div>
      </div>
      <div>
        <Button
          onClick={() => {
            savePreferences.mutate();
          }}
          color="secondary"
          variant="outlined"
          className="w-[220px]"
          loading={savePreferences.isLoading}
          disabled={disableSubmitButton}
        >
          {t('dialog.saveChanges')}
        </Button>
      </div>
    </Container>
  );
};
